/**
 * "Chi nhánh đang dùng" của web quầy (ADR-101) — phần thuần, không React.
 *
 * Vì sao: trước đây MƯỜI chỗ tự gọi `api.branches()`, tự giữ `branchId`, tự lấy chi nhánh ĐẦU — đổi chi nhánh ở Lịch không
 * sang Danh sách / Két / Kho, hồ sơ khách luôn bán gói vào chi nhánh đầu, tải lại trang là về chi nhánh đầu. Chọn lặng lẽ
 * là gốc của "nhầm nơi" (cùng bài học ADR-100 ở Mini App). Giờ chỉ MỘT nguồn: máy nhớ chi nhánh theo tiệm; máy mới tự mở ở
 * chi nhánh CHÍNH của người đăng nhập (không hỏi — ít chạm), dòng tên tiệm luôn nói chi nhánh đang dùng, chạm để đổi.
 *
 * Tiệm một chi nhánh: không đọc, không ghi bộ nhớ, không vẽ gì thêm (giao diện y hệt trước).
 */
import type { Branch } from './api.ts'

/** Người dùng — chỉ phần cần để biết được chọn chi nhánh nào. */
export interface NguoiChonChiNhanh {
  vai: string
  quyen: readonly string[]
  /** Chi nhánh được giao (`staff_branch`), chi nhánh chính trước. Máy chủ cũ không trả → coi như rỗng. */
  chiNhanhCuaToi?: readonly string[] | undefined
}

/**
 * Chi nhánh người này CHỌN ĐƯỢC. Chủ tiệm / quản lý: mọi chi nhánh đang dùng. Vai khác: giao giữa chi nhánh đang dùng và chi
 * nhánh được giao; giao rỗng (chưa giao, máy chủ cũ không trả, hoặc chi nhánh được giao đã ẩn hết) → mọi chi nhánh (hành vi cũ).
 * Đây là tiện cho giao diện, KHÔNG phải quyền: máy chủ vẫn nhận mọi `branchId` của tiệm (ADR-101, Q2).
 */
export function dsChonDuoc<T extends { id: string }>(
  ds: readonly T[],
  nguoi: NguoiChonChiNhanh,
): T[] {
  if (nguoi.vai === 'manager' || nguoi.quyen.includes('cau-hinh.tiem')) return [...ds]
  const giao = nguoi.chiNhanhCuaToi ?? []
  const chon = ds.filter((c) => giao.includes(c.id))
  return chon.length > 0 ? chon : [...ds]
}

/** Kết quả của việc chọn chi nhánh đang dùng. */
export type LucMo =
  /** Tiệm chưa có chi nhánh nào để chọn. */
  | { loai: 'khong-co' }
  /** Đúng một chi nhánh chọn được: dùng nó, không nhớ gì, không vẽ gì thêm. */
  | { loai: 'mot'; id: string }
  /** Nhiều chi nhánh và máy đã nhớ một chi nhánh còn chọn được. */
  | { loai: 'da-nho'; id: string }
  /** Nhiều chi nhánh, máy chưa nhớ (hoặc cái đã nhớ nay đã ẩn): chi nhánh CHÍNH của người dùng, không có thì chi nhánh đầu. Không hỏi. */
  | { loai: 'mac-dinh'; id: string }

/**
 * Chọn chi nhánh đang dùng từ danh sách CHỌN ĐƯỢC, cái máy đã nhớ và các chi nhánh được giao (chính trước).
 * Cũng là luật "sau khi tải lại danh sách": còn thì giữ; mất (vừa ẩn) thì về mặc định; còn đúng một thì dùng nó.
 */
export function chonLucMo(
  dsChon: readonly { id: string }[],
  daNho: string | null,
  chinh: readonly string[] = [],
): LucMo {
  const [dau] = dsChon
  if (dau === undefined) return { loai: 'khong-co' }
  if (dsChon.length === 1) return { loai: 'mot', id: dau.id }
  if (daNho !== null && dsChon.some((c) => c.id === daNho)) return { loai: 'da-nho', id: daNho }
  const chinhConDung = chinh.find((id) => dsChon.some((c) => c.id === id))
  return { loai: 'mac-dinh', id: chinhConDung ?? dau.id }
}

/** Id chi nhánh đang dùng, hoặc '' khi không có. */
export function idTuLuc(luc: LucMo): string {
  return luc.loai === 'khong-co' ? '' : luc.id
}

/** Chỉ cần `getItem` / `setItem` — `localStorage` hoặc bản giả khi test. `null` = không có bộ nhớ. */
export interface KhoNho {
  getItem(khoa: string): string | null
  setItem(khoa: string, giaTri: string): void
}

/** Khoá theo TIỆM: tiệm khác dùng chung máy không thấy chi nhánh của tiệm này. */
export function khoaNho(tiem: string): string {
  return `sapa.quay.chiNhanh.${tiem}`
}

/** Id chi nhánh hợp lệ trông như uuid; giá trị rác (sửa tay, bản cũ) coi như chưa nhớ. */
const DANG_ID = /^[0-9a-fA-F-]{8,64}$/

/** Đọc chi nhánh đã nhớ. Bộ nhớ ném lỗi, trống hay rác → `null` (chưa nhớ). */
export function docNho(kho: KhoNho | null, tiem: string): string | null {
  if (kho === null) return null
  try {
    const v = kho.getItem(khoaNho(tiem))
    return v !== null && DANG_ID.test(v) ? v : null
  } catch {
    return null
  }
}

/** Ghi chi nhánh người dùng vừa chọn. Không ghi được thì thôi: lần sau hỏi lại. */
export function ghiNho(kho: KhoNho | null, tiem: string, id: string): void {
  if (kho === null) return
  try {
    kho.setItem(khoaNho(tiem), id)
  } catch {
    // Chế độ riêng tư / hết chỗ: không nhớ được thì lần sau hỏi lại. Không sao.
  }
}

/** `localStorage` nếu truy cập được (có trình duyệt chặn cả việc chạm vào nó). */
export function khoNhoMay(): KhoNho | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** Lựa chọn của một lần đổi chi nhánh. */
export interface TuyChonDoi {
  /**
   * `false` = chi nhánh tạm (BK-2: mở lịch vướng của chi nhánh khác) — KHÔNG phải người dùng chọn nên không ghi nhớ,
   * tải lại trang là về chi nhánh đã nhớ (cùng bài học ADR-100 vòng 1 với link `cn`). Mặc định `true`.
   */
  nho?: boolean
}

/** Có ghi nhớ lần đổi này không? */
export function coGhiNho(tuyChon: TuyChonDoi | undefined): boolean {
  return tuyChon?.nho !== false
}

/** Phần của "chi nhánh đang dùng" mà chuông cần (khớp `ChiNhanhGiaTri` trong `chi-nhanh-dang-dung.tsx`). */
export interface CnChoChuong {
  nhieu: boolean
  id: string
  ten: string
  ds: readonly { id: string; name: string }[] | null
}

/** Bấm một tin trong chuông thì chi nhánh đang dùng ra sao. */
export type DichCuaTin =
  /** Một chi nhánh / cùng chi nhánh đang dùng: không đổi gì. */
  | { loai: 'giu' }
  /** Danh sách chi nhánh CHƯA tải xong: chưa biết đổi được không → giữ lựa chọn chờ, áp lại khi danh sách về (vòng 1 B). */
  | { loai: 'cho' }
  /** Chi nhánh khác và chọn được: đổi TẠM (không ghi nhớ), kèm tên cả hai để báo "Đã chuyển sang … / Về …". */
  | { loai: 'doi'; id: string; tuTen: string; denTen: string }
  /** Không có trong danh sách chọn được (đã ẩn / không được giao): đi như trước, không đổi chi nhánh. */
  | { loai: 'khong-co' }

/** Chuông (ADR-101 phụ lục): tin của chi nhánh khác thì mở ĐÚNG chi nhánh đó — gốc sự cố 31/10 "bấm tin thấy lịch trống". */
export function dichCuaTin(
  branchId: string,
  cn: Pick<CnChoChuong, 'nhieu' | 'id' | 'ten' | 'ds'>,
): DichCuaTin {
  if (cn.ds === null) return { loai: 'cho' }
  if (!cn.nhieu || branchId === cn.id) return { loai: 'giu' }
  const den = cn.ds.find((c) => c.id === branchId)
  if (den === undefined) return { loai: 'khong-co' }
  return { loai: 'doi', id: den.id, tuTen: cn.ten, denTen: den.name }
}

/**
 * Chạy một lần tải theo chi nhánh trong `useEffect`: trả hàm dọn (`return taiCoHuy(...)`). Sau khi dọn — tức là chi nhánh
 * (hoặc màn) đã đổi — kết quả HOẶC lỗi của lần cũ về muộn bị bỏ, không đè danh sách của chi nhánh mới dưới tên chi nhánh mới
 * (vòng 1 A: `?mo=cho&cn=` gửi hai yêu cầu liền nhau; cùng mẫu cờ `con` của GoiVi.tsx).
 */
export function taiCoHuy<T>(
  tai: Promise<T>,
  khiXong: (kq: T) => void,
  khiLoi: (e: unknown) => void,
): () => void {
  let con = true
  tai.then(
    (kq) => {
      if (con) khiXong(kq)
    },
    (e: unknown) => {
      if (con) khiLoi(e)
    },
  )
  return () => {
    con = false
  }
}

/** Một dòng tóm tắt đầu ngăn chuông: "n tin mới ở <chi nhánh>". */
export interface TomTatCn {
  id: string
  ten: string
  n: number
}

/**
 * Tin MỚI (sau mốc đã xem) ở các chi nhánh KHÁC chi nhánh đang dùng, gom theo chi nhánh, nhiều tin trước.
 * Tiệm một chi nhánh, hoặc chi nhánh không chọn được → không đếm (không vẽ gì thêm).
 */
export function tomTatTinMoi(
  tin: readonly { branchId: string; luc: string }[],
  daXemMs: number,
  cn: Pick<CnChoChuong, 'nhieu' | 'id' | 'ds'>,
): TomTatCn[] {
  if (!cn.nhieu || cn.ds === null) return []
  const dem = new Map<string, number>()
  for (const t of tin) {
    if (t.branchId === cn.id || Date.parse(t.luc) <= daXemMs) continue
    dem.set(t.branchId, (dem.get(t.branchId) ?? 0) + 1)
  }
  const ra: TomTatCn[] = []
  for (const c of cn.ds) {
    const n = dem.get(c.id)
    if (n !== undefined) ra.push({ id: c.id, ten: c.name, n })
  }
  return ra.sort((a, b) => b.n - a.n)
}

/** `?cn=` trên đường dẫn là đầu vào KHÔNG tin cậy: chỉ nhận chuỗi trông như id chi nhánh (rồi vẫn phải có trong danh sách chọn được). */
export function laIdChiNhanh(v: unknown): v is string {
  return typeof v === 'string' && DANG_ID.test(v)
}

/** Tên chi nhánh đang dùng ('' khi chưa có). */
export function tenChiNhanh(ds: readonly Branch[] | null, id: string): string {
  return ds?.find((c) => c.id === id)?.name ?? ''
}

/**
 * Hai danh sách chi nhánh giống nhau (cùng id, tên, thứ tự)? Lịch tải lại danh sách mỗi lần thử lại — giống nhau thì
 * không đặt lại state, các màn không vẽ lại vô cớ.
 */
export function cungDanhSach(a: readonly Branch[] | null, b: readonly Branch[]): boolean {
  return (
    a !== null &&
    a.length === b.length &&
    a.every((c, i) => c.id === b[i]?.id && c.name === b[i]?.name)
  )
}

/**
 * Chi nhánh cho hồ sơ khách (bán gói / nạp ví / hoàn): đúng chi nhánh đang dùng. Trước đây luôn là chi nhánh ĐẦU
 * (`branches[0]`) nên tiền vào nhầm két. `null` = chưa có chi nhánh nào để ghi (không mở được hồ sơ ghi tiền).
 */
export function chiNhanhChoHoSoKhach(idDangDung: string): string | null {
  return idDangDung === '' ? null : idDangDung
}

/**
 * Chi nhánh mặc định của một tờ ghi tiền có ô chọn chi nhánh riêng (Bán lẻ, Ghi khoản chi, Hoa hồng tiền mặt, Trả bo):
 * chi nhánh đang dùng; chưa có thì chi nhánh đầu của danh sách — tờ luôn HIỆN ô chọn khi nhiều chi nhánh nên người ghi
 * thấy tên (không chọn lặng lẽ).
 */
export function chiNhanhMacDinhCuaTo(ds: readonly Branch[] | null, idDangDung: string): string {
  if (idDangDung !== '') return idDangDung
  return ds?.[0]?.id ?? ''
}

/** Trạng thái "mọi tờ đóng" khi đổi chi nhánh giữa chừng — màn Lịch đặt lại từng ô bằng đúng các giá trị này. */
export interface ToDongHet {
  chon: null
  dienSan: null
  goiViCho: null
  doiGioCho: null
  hoanTatCho: null
  moDat: false
  moKhachVao: false
  moTinhTien: false
  moNghi: null
}

/**
 * Đổi chi nhánh (hiếm: chi nhánh đang dùng vừa bị ẩn / BK-2) khi tờ đặt lịch, thu tiền, khách vào… còn mở: đóng hết,
 * để không tờ nào ghi vào chi nhánh khác với cái nó được mở cho. KHÔNG đụng khoá chống lặp (nằm ở từng tờ; gửi lại cùng
 * khoá vẫn được máy chủ nhận ra — bài học ADR-100).
 */
export function donKhiDoiChiNhanh(): ToDongHet {
  return {
    chon: null,
    dienSan: null,
    goiViCho: null,
    doiGioCho: null,
    hoanTatCho: null,
    moDat: false,
    moKhachVao: false,
    moTinhTien: false,
    moNghi: null,
  }
}
